import {
  type InterviewLanguage,
  LANGUAGE_DISPLAY_NAMES,
} from "@repo/app-types/constants/languages";
import {
  getErrorMessageForAssessmentCriteriaValue,
  getSpecialAssessmentField,
  isCefrLanguageAssessmentLanguage,
  LanguageToCefrAssessmentFieldName,
  type SpecialAssessmentFieldName,
} from "@repo/app-types/entities/assessment";
import type {
  CustomFieldDefinition,
  CustomFieldDefinitionInsertClient,
} from "@repo/app-types/entities/customField";
import {
  type CustomFieldDefinitionImportanceSetting,
  CustomFieldResourceType,
  CustomFieldType,
  specialCustomFieldDetails,
} from "@repo/app-types/entities/customField";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import {
  type DropdownItem,
  DropdownMenu,
} from "@repo/ui/Dropdown/dropdownMenu";
import { Size } from "@repo/ui/helpers/helpers";
import { IconPlus } from "@tabler/icons-react";
import type React from "react";
import { MenuTrigger } from "react-aria-components";
import { Can } from "../../helpers/teammateAuthorizationContext.js";
import { AssessmentEditorRow } from "./assessmentEditorRow.js";

const MAX_FIELDS = 12;

interface AssessmentEditorProps {
  interviewLanguage: InterviewLanguage;
  fields: (CustomFieldDefinition | CustomFieldDefinitionInsertClient)[];
  setFields: (
    fields: (CustomFieldDefinition | CustomFieldDefinitionInsertClient)[],
  ) => void;
  fieldErrors: string[];
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>;
}

export const AssessmentEditor: React.FC<AssessmentEditorProps> = ({
  interviewLanguage,
  fields,
  setFields,
  fieldErrors,
  setFieldErrors,
}) => {
  const defaultFieldType = CustomFieldType.Boolean;

  const onAddField = (specialFieldName?: SpecialAssessmentFieldName) => {
    if (fields.length >= MAX_FIELDS) {
      return;
    }

    const specialFieldDetails = specialFieldName
      ? specialCustomFieldDetails[specialFieldName]
      : null;

    const newField: CustomFieldDefinitionInsertClient = {
      display_name: specialFieldDetails?.display_name || "",
      field_name: specialFieldName || "",
      field_type: specialFieldDetails?.field_type || defaultFieldType,
      provider: "alpharun",
      resource_type: CustomFieldResourceType.INTERVIEW,
    };

    // Add special fields to the top of the list
    setFields(
      specialFieldDetails ? [newField, ...fields] : [...fields, newField],
    );
  };

  const onRemoveField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
    setFieldErrors(fieldErrors.filter((_, i) => i !== index));
  };

  const onRenameField = (index: number, newName: string) => {
    // When we rename a field we're effectively creating a new custom field definition
    setFields(
      fields.map((field, i) => {
        if (i !== index) {
          return field;
        }

        return {
          display_name: newName,
          field_name: newName,
          resource_type: CustomFieldResourceType.INTERVIEW,
          provider: "alpharun",
          field_type: field.field_type,
          settings: field.settings,
        };
      }),
    );
  };

  const validateFieldOnBlur = (index: number, value: string) => {
    const error = getErrorMessageForAssessmentCriteriaValue(value);
    setFieldErrors((prev) => {
      const newErrors = [...prev];
      newErrors[index] = error;
      return newErrors;
    });
  };

  const onChangeImportance = (
    index: number,
    importance: CustomFieldDefinitionImportanceSetting | undefined,
  ) => {
    setFields(
      fields.map((field, i) => {
        if (i !== index) {
          return field;
        }

        return {
          display_name: field.display_name,
          field_name: field.field_name,
          resource_type: CustomFieldResourceType.INTERVIEW,
          provider: "alpharun",
          field_type: field.field_type,
          settings: {
            ...(field.settings || {}),
            importance,
          },
        };
      }),
    );
  };

  const renderTableRows = () => {
    if (fields.length === 0) {
      return (
        <tr>
          <td
            className="px-3 py-9 text-center text-sm font-semibold text-foreground"
            colSpan={3}
          >
            No job qualifications added yet
          </td>
        </tr>
      );
    }

    return fields.map((field, index) => (
      <AssessmentEditorRow
        field={field}
        fieldError={fieldErrors[index] ?? ""}
        key={`assessment-field-${index}`}
        onBlur={(value: string) => {
          validateFieldOnBlur(index, value);
        }}
        onRemoveField={() => {
          onRemoveField(index);
        }}
        onRenameField={(newName: string) => {
          onRenameField(index, newName);
        }}
        onChangeImportance={(
          importance: CustomFieldDefinitionImportanceSetting | undefined,
        ) => {
          onChangeImportance(index, importance);
        }}
        placeholder="Has 2+ years of customer service experience"
      />
    ));
  };

  const languageFieldName = isCefrLanguageAssessmentLanguage(interviewLanguage)
    ? LanguageToCefrAssessmentFieldName[interviewLanguage]
    : undefined;
  const existingLanguageLevelField = languageFieldName
    ? getSpecialAssessmentField(fields, languageFieldName)
    : undefined;
  // Show the language level field if the interview language is a Cefr language (i.e. not for English/Spanish multilingual)
  const newLanguageOption = isCefrLanguageAssessmentLanguage(interviewLanguage)
    ? {
        label: `CEFR ${
          LANGUAGE_DISPLAY_NAMES[interviewLanguage] || "Language"
        } level`,
        onClick: () => {
          onAddField(languageFieldName);
        },
      }
    : null;

  return (
    <div>
      <div>
        <table className="min-w-full">
          <tbody className="bg-white">{renderTableRows()}</tbody>
        </table>
      </div>
      <div className="px-3 py-2 text-sm text-gray-600">
        The key qualifications that candidates need to get this role.
      </div>
      <div className="mt-3 mb-3 ml-3 flex">
        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.JOB_OPENINGS}
          passThrough={true}
        >
          {(allowed) => (
            <div className="relative inline-block">
              <MenuTrigger>
                <Button
                  icon={<IconPlus className="h-4 w-4" />}
                  isDisabled={fields.length >= MAX_FIELDS || !allowed}
                  size={Size.MEDIUM}
                  variant={ButtonVariant.Secondary}
                >
                  {"Add job qualification"}
                </Button>
                <DropdownMenu
                  align="left"
                  position="top"
                  items={[
                    {
                      label: "Yes/No criteria",
                      onClick: () => {
                        onAddField();
                      },
                    },
                    existingLanguageLevelField ? null : newLanguageOption,
                  ].filter((item): item is DropdownItem => item !== null)}
                />
              </MenuTrigger>
            </div>
          )}
        </Can>
      </div>
    </div>
  );
};
